import React, { useState, useEffect } from 'react';
import { useCPRLog } from './CPRLog';
import { useResusContext } from '../Resus/ResusContext';
import { loadCurrentState, saveCurrentState } from './CprState/storage';
import AirwaysRecommendedValues from './AirwaysRecommendedValues';
import { cprEventEmitter, EVENTS } from './cprEvents';
import './Airways.css';
import IntubationChecklist from './IntubationChecklist';
import { getHospitalData } from './PerHospitalResusConfig';
import { useTranslation } from 'react-i18next';

interface AirwaysProps {
  onCollapse?: () => void;
}

interface AirwaysData {
  airwayType: 'טובוס' | 'טובוס עם בלונית' | 'LMA' | null;
  airwaySize: string;
  airwayDepth: string;
  hasOropharingealAirway: boolean;
  hasChestDrain: boolean;
  chestDrainSize: string;
  hasSurgicalAirway: boolean;
}

interface RecommendedValues {
  blade: number;
  cuffedETT: number;
  ETT: number;
  lma: number;
}

const initialData: AirwaysData = {
  airwayType: null,
  airwaySize: '',
  airwayDepth: '',
  hasOropharingealAirway: false,
  hasChestDrain: false,
  chestDrainSize: '',
  hasSurgicalAirway: false
};

const Airways: React.FC<AirwaysProps> = ({ onCollapse }) => {
  const { addEntry } = useCPRLog();
  const { age, weight, hospital } = useResusContext();
  const { t } = useTranslation(['resus']);
  const [recommendedValues, setRecommendedValues] = useState<RecommendedValues>({} as RecommendedValues);
  const [data, setData] = useState<AirwaysData>(() => {
    const savedState = loadCurrentState();
    return savedState?.airways || initialData;
  });

  const airwaysData = getHospitalData(hospital).airways;

  useEffect(() => {
    if (age !== null && age !== undefined && airwaysData.dataByAge) {
      const currData = airwaysData.dataByAge.find((data: any) => data.age === age);
      if (currData) {
        setRecommendedValues(currData);
      }
    }
  }, [age, airwaysData.dataByAge]);

  useEffect(() => {
    const unsubscribe = cprEventEmitter.subscribe(EVENTS.RESET_CPR, () => {
      setData(initialData);
    });

    return () => unsubscribe();
  }, []);

  const getChestTubeSize = (ageInMonths: number | null): string => {
    if (ageInMonths === null) return "";
    if (ageInMonths < 3) return "8-12";
    if (ageInMonths < 36) return "12-16";
    if (ageInMonths < 144) return "16-24";
    return "20-32";
  };

  const getAverageFromRange = (range: string): string => {
    if (!range) return "";
    const [min, max] = range.split('-').map(Number);
    return String(Math.round((min + max) / 2));
  };

  const calculateDefaultDepth = (size: string): string => {
    const numericSize = parseFloat(size);
    if (!isNaN(numericSize)) {
      return Math.floor(numericSize * 3).toString();
    }
    return '';
  };

  const handleAirwayTypeChange = (type: AirwaysData['airwayType']) => {
    let defaultSize = '';
    if (type === 'טובוס') {
      defaultSize = String(recommendedValues.ETT || '');
    } else if (type === 'טובוס עם בלונית') {
      defaultSize = String(recommendedValues.cuffedETT || '');
    } else if (type === 'LMA') {
      defaultSize = String(recommendedValues.lma || '');
    }
    
    const newDepth = calculateDefaultDepth(defaultSize);

    setData(prev => ({
      ...prev,
      airwayType: type,
      airwaySize: defaultSize,
      airwayDepth: newDepth
    }));
  };

  const handleSizeChange = (size: string) => {
    const newDepth = calculateDefaultDepth(size);
    setData(prev => ({
      ...prev,
      airwaySize: size,
      airwayDepth: newDepth
    }));
  };

  const handleChestDrainChange = (checked: boolean) => {
    let defaultSize = '';
    if (checked && age !== null && age !== undefined) {
      const sizeRange = getChestTubeSize(age);
      defaultSize = getAverageFromRange(sizeRange);
    }
    
    setData(prev => ({
      ...prev,
      hasChestDrain: checked,
      chestDrainSize: defaultSize
    }));
  };

  const handleConfirm = () => {
    const savedState = loadCurrentState();
    const previousData = savedState?.airways || initialData;
    
    // Save current state first
    saveCurrentState({ airways: data }, 'airways');
  
    // Compare and log only changed values
    if (data.airwayType !== previousData.airwayType || 
        data.airwaySize !== previousData.airwaySize ||
        data.airwayDepth !== previousData.airwayDepth) {
      if (data.airwayType) {
        let logText = t('airways.logConnected', {
          type: t(`airway.${data.airwayType === 'טובוס' ? 'tubeOnly' : data.airwayType === 'טובוס עם בלונית' ? 'tubeWithBalloon' : 'lma'}`),
          size: data.airwaySize || t('airways.notEntered')
        });
        
        if ((data.airwayType === 'טובוס' || data.airwayType === 'טובוס עם בלונית') && data.airwayDepth) {
          logText += t('airways.logDepth', { depth: data.airwayDepth });
        }
        
        addEntry({
          timestamp: new Date().toISOString(),
          text: logText,
          type: 'action',
          isImportant: false
        });
      }
    }
  
    if (data.hasChestDrain !== previousData.hasChestDrain || 
        (data.hasChestDrain && data.chestDrainSize !== previousData.chestDrainSize)) {
      if (data.hasChestDrain) {
        addEntry({
          timestamp: new Date().toISOString(),
          text: t('airways.logChestDrainConnected', { size: data.chestDrainSize || t('airways.notEntered') }),
          type: 'action',
          isImportant: false
        });
      }
    }
  
    if (data.hasSurgicalAirway !== previousData.hasSurgicalAirway && data.hasSurgicalAirway) {
      addEntry({
        timestamp: new Date().toISOString(),
        text: t('airways.logSurgicalAirway'),
        type: 'action',
        isImportant: false
      });
    }

    if (data.hasOropharingealAirway !== previousData.hasOropharingealAirway && data.hasOropharingealAirway) {
      addEntry({
        timestamp: new Date().toISOString(),
        text: t('airways.logOropharyngealAirway'),
        type: 'action',
        isImportant: false
      });
    }
  
    onCollapse?.();
  };

  return (
    <div className="airways">
      {age !== null && age !== undefined && weight !== null && weight !== undefined ? (
        <AirwaysRecommendedValues />
      ) : (
        <div style={{ color: 'red', textAlign: 'right', marginBottom: '20px', fontWeight: 'bold' }}>
          {t('airways.enterAgeWeightPrompt')}
        </div>
      )}

      <IntubationChecklist />
      
      <form style={{padding: '10px'}}>
        <div className="input-group full-width">
          <label>{t('airway.title')}:</label>
          <div className="radio-options">
            <label className="radio-label">
              <input                
                type="radio"
                name="airwayType"
                value="טובוס עם בלונית"
                checked={data.airwayType === 'טובוס עם בלונית'}
                onChange={(e) => handleAirwayTypeChange(e.target.value as 'טובוס עם בלונית')}
                data-testid="airway-type-cuffed-tube"
              />
              {t('airway.tubeWithBalloon')}
            </label>
            <label className="radio-label">
              <input
                type="radio"
                name="airwayType"
                value="טובוס"
                checked={data.airwayType === 'טובוס'}
                onChange={(e) => handleAirwayTypeChange(e.target.value as 'טובוס')}
                data-testid="airway-type-tube"
              />
              {t('airway.tubeOnly')}
            </label>
            <label className="radio-label">
              <input
                type="radio"
                name="airwayType"
                value="LMA"
                checked={data.airwayType === 'LMA'}
                onChange={(e) => handleAirwayTypeChange(e.target.value as 'LMA')}
                data-testid="airway-type-lma"
              />
              {t('airways.lma')}
            </label>
          </div>
        </div>

        {data.airwayType && (
          <div className="input-group indented narrow">
            <label>{t('airway.size')}:</label>
            <input
              type="number"
              inputMode='decimal'
              value={data.airwaySize}
              onFocus={(e) => e.target.select()}
              onChange={(e) => handleSizeChange(e.target.value)}
              data-testid="airway-size-input"
            />
          </div>
        )}

        {(data.airwayType === 'טובוס' || data.airwayType === 'טובוס עם בלונית') && (
          <div className="input-group indented narrow">
            <label>{t('airway.depth')}:</label>
            <input
              type="number"
              inputMode='decimal'
              value={data.airwayDepth}
              onFocus={(e) => e.target.select()}
              onChange={(e) => setData(prev => ({ ...prev, airwayDepth: e.target.value }))}
              data-testid="airway-depth-input"
            />
          </div>
        )}

        <div className="input-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={data.hasOropharingealAirway}
              onChange={(e) => setData(prev => ({ ...prev, hasOropharingealAirway: e.target.checked }))}
              data-testid="oropharingeal-airway-checkbox"
            />
            {t('airways.oropharyngealAirway')}
          </label>
        </div>

        <div className="input-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={data.hasSurgicalAirway}
              onChange={(e) => setData(prev => ({ ...prev, hasSurgicalAirway: e.target.checked }))}
              data-testid="surgical-airway-checkbox"
            />
            {t('airways.surgicalAirway')}
          </label>
        </div>

        <div className="input-group full-width">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={data.hasChestDrain}
              onChange={(e) => handleChestDrainChange(e.target.checked)}
              data-testid="chest-drain-checkbox"
            />
            {t('airways.chestTube')}
          </label>
        </div>

        {data.hasChestDrain && (
          <div className="input-group indented narrow">
            <label>{t('airway.size')}:</label>
            <input
              type="number"
              placeholder={t('airways.sizeInFr')}
              value={data.chestDrainSize}
              onFocus={(e) => e.target.select()}
              onChange={(e) => setData(prev => ({ ...prev, chestDrainSize: e.target.value }))}
              data-testid="chest-drain-size-input"
            />
          </div>
        )}

        <div className="button-container">
          <button 
            type="button" 
            className='btn btn-update' 
            onClick={handleConfirm}
            data-testid="airways-confirm-button"
          >
            {t('buttons.confirm')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default Airways;